import { instance } from "api/apiBase";
import type { OwnerScreeningReport } from "api/owner-screening-report";

export const QUERY_OWNER_REPORT_SETTINGS = ["OWNER_REPORT_SETTINGS"];

export type ApplicantNameDisplay = "first_name_last_initial" | "full_name";
export type LinkExpiryDays = 7 | 14 | 30;

/** Totals across everyone on the application, shown above the per-person cards. */
export interface OwnerReportTotals {
  show_application_rent: boolean;
  show_desired_move_in: boolean;
  show_recommendation: boolean;
  show_desired_lease_term: boolean;
  show_verified_gross_income: boolean;
  show_verified_gross_income_to_rent: boolean;
  show_debt_to_verified_gross_income: boolean;
  show_verified_net_income: boolean;
  show_verified_net_income_to_rent: boolean;
  show_average_credit_score: boolean;
  show_lowest_credit_score: boolean;
  show_highest_credit_score: boolean;
  show_people_with_no_credit_score: boolean;
  include_guarantors_in_totals: boolean;
}

export interface OwnerReportPeople {
  include_applicants: boolean;
  include_guarantors: boolean;
  include_occupants: boolean;
}

export interface OwnerReportSections {
  section_identity_verification: boolean;
  section_income: boolean;
  section_credit: boolean;
  section_criminal: boolean;
  section_housing_court: boolean;
  section_rental_verification: boolean;
  section_household: boolean;
  section_housing_history: boolean;
  section_warning_signals: boolean;
}

export interface OwnerReportSettings {
  report_title: string;
  totals: OwnerReportTotals;
  people: OwnerReportPeople;
  sections: OwnerReportSections;
  display: { applicant_name_display: ApplicantNameDisplay };
  sharing: { link_expiry_days: LinkExpiryDays; allow_message: boolean };
  /** Desired lease term is only offered to partners using Yardi lease terms. */
  lease_term_available: boolean;
  /** False until the partner saves for the first time; the API still returns defaults. */
  configured: boolean;
  /**
   * Sections this reader's own role cannot see, and so cannot share. "What
   * owners will see" subtracts them: it has to list what will be sent, not what
   * the partner switched on.
   */
  restricted_sections: string[];
  /** Totals this reader cannot share either — drawn from the same reports. */
  restricted_metrics: string[];
}

/** The PUT accepts any subset, so one toggle can be saved without echoing the rest. */
export type OwnerReportSettingsPayload = Partial<
  OwnerReportTotals &
    OwnerReportPeople &
    OwnerReportSections & {
      report_title: string;
      applicant_name_display: ApplicantNameDisplay;
      link_expiry_days: LinkExpiryDays;
      allow_message: boolean;
    }
>;

export const getOwnerReportSettings = async () => {
  const { data } = await instance.get<OwnerReportSettings>(
    "/portal/v1/owner_report_settings"
  );
  return data;
};

/**
 * The report these settings would produce, rendered from a sample application —
 * the setup page has none of its own. Attributes are applied without saving, so
 * the page can preview before Save.
 */
export const previewOwnerReportSettings = async (
  payload: OwnerReportSettingsPayload
) => {
  const { data } = await instance.post<{ report: OwnerScreeningReport }>(
    "/portal/v1/owner_report_settings/preview",
    payload
  );
  return data.report;
};

export const updateOwnerReportSettings = async (
  payload: OwnerReportSettingsPayload
) => {
  const { data } = await instance.put<OwnerReportSettings>(
    "/portal/v1/owner_report_settings",
    payload
  );
  return data;
};
